import { useQuery } from '@apollo/client';
import { AddCircle, VerifiedUser } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import type { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { PageContainer } from '~/components/page-layout';
import { SearchContainer } from '~/components/search';
import { Toolbar } from '~/components/toolbar';
import { FolderContextProvider, useAppContext } from '~/contexts';
import { useTabs } from '../lib/tabs';
import { RolePlaylistFolderListDocument } from './RolePlaylistFolderList.generated';
import { AddPlaylistFoldersDialog, BulkActionsMenu, PlaylistFolderTable } from './components';

export const RolePlaylistFolderList = () => {
  const { t } = useTranslation();
  const params = useParams<{ roleId: string }>();
  const roleId = parseInt(params.roleId ?? '');

  const { currentNetwork } = useAppContext();
  const tabs = useTabs();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { data, loading, error } = useQuery(RolePlaylistFolderListDocument, {
    variables: { networkId: currentNetwork.id, roleId, search },
  });

  const [addOpen, setAddOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);

  useEffect(() => {
    if (!data?.network?.role.playlistFolderRules) return;
    const ruleIds = new Set<GridRowId>(data.network.role.playlistFolderRules.map((x) => x.id));
    if (selectedIds.every((id) => ruleIds.has(id))) return;
    setSelectedIds(selectedIds.filter((id) => ruleIds.has(id)));
  }, [data?.network?.role.playlistFolderRules, selectedIds]);

  if ((!loading && !data) || error) return <Navigate to="/not-found" />;

  const addPlaylistFoldersButton = (
    <Button
      color="primary"
      onClick={() => setAddOpen(true)}
      startIcon={<AddCircle />}
      variant="contained"
    >
      {t('pages.roles.buttons.add_playlist_folders')}
    </Button>
  );

  const rules = data?.network?.role.playlistFolderRules ?? [];

  return (
    <>
      {data?.network && (
        <FolderContextProvider folderTree={data.network.playlistFolderTree}>
          <Toolbar
            actions={addPlaylistFoldersButton}
            breadcrumbsLabel={<RouterBreadcrumbs />}
            titleIcon={<VerifiedUser />}
            titleText={data.network.role.name}
          />

          <ScrollableRouterTabs current={t('pages.roles.tabs.playlist_folders')} tabs={tabs} />

          <PageContainer>
            <SearchContainer>
              <SearchBar
                placeholder={t('pages.roles.playlist_folders.search_placeholder')}
                search={search}
              />
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 4 }}>
                <BulkActionsMenu role={data.network.role} selectedIds={selectedIds} />
                <Count
                  selectedCount={selectedIds.length}
                  totalCount={rules.length}
                  thing="pages.roles.playlist_folders.thing_plural"
                />
              </Box>
            </SearchContainer>
            <PlaylistFolderTable
              checkboxSelection={rules.length > 1}
              disableRowSelectionOnClick={rules.length < 2}
              loading={loading}
              onRowSelectionModelChange={setSelectedIds}
              role={data.network.role}
              rows={data.network.role.playlistFolderRules}
              rowSelectionModel={selectedIds}
            />
          </PageContainer>

          {addOpen && (
            <AddPlaylistFoldersDialog
              fullWidth
              onClose={() => setAddOpen(false)}
              open
              role={data.network.role}
            />
          )}
        </FolderContextProvider>
      )}
    </>
  );
};
